import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { 
  Camera, 
  Plus, 
  MapPin, 
  Calendar, 
  Scale, 
  Ruler, 
  Fish,
  Heart,
  Trash2,
  Upload,
  X,
  Users,
  Lock,
  Globe
} from "lucide-react";

const FISHING_TYPES = ["Fresh Water", "Specimen", "Bass", "Fly Fishing", "Rock and Surf", "Boat", "Offshore", "Estuary"];

const FishingLogPage = () => {
  const { user, token } = useAuth();
  const [catches, setCatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    species: "",
    weight: "",
    length: "",
    location: "",
    fishing_type: "",
    notes: "",
    date_caught: new Date().toISOString().split('T')[0],
    share_to_community: true
  });

  useEffect(() => {
    fetchCatches();
  }, []);

  const fetchCatches = async () => {
    try {
      const response = await axios.get(`${API}/catches/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCatches(response.data);
    } catch (error) {
      toast.error("Failed to fetch catches");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error(`Photo too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum size is 5MB.`);
        e.target.value = "";
        return;
      }
      
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a JPG, PNG, or WEBP image.");
        e.target.value = "";
        return;
      }
      
      setSelectedPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const clearPhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.species || !formData.location || !formData.fishing_type || !formData.date_caught) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    try {
      const form = new FormData();
      form.append("species", formData.species);
      form.append("location", formData.location);
      form.append("fishing_type", formData.fishing_type);
      form.append("date_caught", formData.date_caught);
      
      if (formData.weight) form.append("weight", formData.weight);
      if (formData.length) form.append("length", formData.length);
      if (formData.notes) form.append("notes", formData.notes);
      if (selectedPhoto) form.append("photo", selectedPhoto);
      form.append("share_to_community", formData.share_to_community);

      await axios.post(`${API}/catches`, form, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success("Catch logged successfully!");
      setDialogOpen(false);
      setFormData({
        species: "",
        weight: "",
        length: "",
        location: "",
        fishing_type: "",
        notes: "",
        date_caught: new Date().toISOString().split('T')[0],
        share_to_community: true
      });
      clearPhoto();
      setUploadProgress(0);
      fetchCatches();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to log catch. Please try again.";
      toast.error(message);
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (catchId) => {
    if (!window.confirm("Are you sure you want to delete this catch?")) return;

    try {
      await axios.delete(`${API}/catches/${catchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Catch deleted");
      fetchCatches();
    } catch (error) {
      toast.error("Failed to delete catch");
    }
  };

  const getFishingTypeBadgeClass = (type) => {
    const classes = {
      "Fresh Water": "bg-blue-100 text-blue-800",
      "Specimen": "bg-purple-100 text-purple-800",
      "Bass": "bg-green-100 text-green-800",
      "Fly Fishing": "bg-amber-100 text-amber-800",
      "Rock and Surf": "bg-gray-100 text-gray-800",
      "Boat": "bg-cyan-100 text-cyan-800",
      "Offshore": "bg-indigo-100 text-indigo-800",
      "Estuary": "bg-teal-100 text-teal-800"
    };
    return classes[type] || "bg-gray-100 text-gray-800";
  };

  return (
    <Layout title="Fishing Log" subtitle="Track and celebrate your catches">
      <div className="space-y-6">
        {/* Header with Add Button */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[var(--river-stone)]">
              {catches.length} {catches.length === 1 ? "catch" : "catches"} logged
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-catch-btn">
                <Plus className="w-4 h-4 mr-2" />
                Log a Catch
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-[var(--deep-ocean)]">Log a New Catch</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Photo Upload */}
                <div className="space-y-2">
                  <Label>Photo (Optional)</Label>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all ${
                      photoPreview ? "border-[var(--deep-ocean)]" : "border-gray-300 hover:border-[var(--deep-ocean)]"
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {photoPreview ? (
                      <div className="relative">
                        <img 
                          src={photoPreview} 
                          alt="Preview" 
                          className="max-h-40 mx-auto rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); clearPhoto(); }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="py-4">
                        <Upload className="w-8 h-8 text-[var(--river-stone)] mx-auto mb-2" />
                        <p className="text-sm text-[var(--river-stone)]">Click to upload photo</p>
                        <p className="text-xs text-[var(--river-stone)] mt-1">Max 5MB • JPG, PNG, WEBP</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoChange}
                    className="hidden"
                    data-testid="photo-input"
                  />
                </div>

                {/* Species */}
                <div className="space-y-2">
                  <Label htmlFor="species">Species *</Label>
                  <Input
                    id="species"
                    name="species"
                    placeholder="e.g., Yellowfish, Cob, Bass"
                    value={formData.species}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="species-input"
                  />
                </div>

                {/* Weight & Length */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="weight">Weight (kg)</Label>
                    <Input
                      id="weight"
                      name="weight"
                      type="number"
                      step="0.1"
                      placeholder="e.g., 2.5"
                      value={formData.weight}
                      onChange={handleChange}
                      className="input-field"
                      data-testid="weight-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="length">Length (cm)</Label>
                    <Input
                      id="length"
                      name="length"
                      type="number"
                      placeholder="e.g., 45"
                      value={formData.length}
                      onChange={handleChange}
                      className="input-field"
                      data-testid="length-input"
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    name="location"
                    placeholder="e.g., Vaal Dam, Durban Pier"
                    value={formData.location}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="location-input"
                  />
                </div>

                {/* Fishing Type */}
                <div className="space-y-2">
                  <Label>Fishing Type *</Label>
                  <Select 
                    value={formData.fishing_type} 
                    onValueChange={(value) => setFormData({ ...formData, fishing_type: value })}
                  >
                    <SelectTrigger className="input-field" data-testid="fishing-type-select">
                      <SelectValue placeholder="Select fishing type" />
                    </SelectTrigger>
                    <SelectContent>
                      {FISHING_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <Label htmlFor="date_caught">Date Caught *</Label>
                  <Input
                    id="date_caught"
                    name="date_caught"
                    type="date"
                    value={formData.date_caught}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="date-input"
                  />
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    placeholder="Any additional details about your catch..."
                    value={formData.notes}
                    onChange={handleChange}
                    className="input-field min-h-[80px]"
                    data-testid="notes-input"
                  />
                </div>

                {/* Share to Community Toggle */}
                <div className="flex items-center justify-between p-4 bg-[var(--bg-main)] rounded-lg">
                  <div className="flex items-center gap-3">
                    {formData.share_to_community ? (
                      <Globe className="w-5 h-5 text-[var(--deep-ocean)]" />
                    ) : (
                      <Lock className="w-5 h-5 text-[var(--river-stone)]" />
                    )}
                    <div>
                      <p className="font-medium text-[var(--deep-ocean)]">
                        {formData.share_to_community ? "Share to Community" : "Keep Private"}
                      </p>
                      <p className="text-xs text-[var(--river-stone)]">
                        {formData.share_to_community 
                          ? "Other members will see this catch" 
                          : "Only you can see this catch"}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.share_to_community}
                    onCheckedChange={(checked) => setFormData({ ...formData, share_to_community: checked })}
                    data-testid="share-toggle"
                  />
                </div>

                {/* Upload Progress */}
                {submitting && uploadProgress > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[var(--river-stone)]">Uploading...</span>
                      <span className="text-[var(--deep-ocean)] font-medium">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={submitting}
                  data-testid="submit-catch-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      {uploadProgress > 0 ? `Uploading ${uploadProgress}%` : "Saving..."}
                    </span>
                  ) : "Log Catch"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Catches Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : catches.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {catches.map((catchItem, index) => (
              <motion.div
                key={catchItem.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`bg-white card-shadow border-0 overflow-hidden catch-card ${
                  catchItem.is_member_of_month_catch ? "ring-4 ring-yellow-400" : ""
                }`}>
                  {catchItem.photo_url && (
                    <div className="relative h-48 bg-gray-100">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${catchItem.photo_url}`}
                        alt={catchItem.species}
                        className="w-full h-full object-cover"
                      />
                      {catchItem.is_member_of_month_catch && (
                        <Badge className="absolute top-3 left-3 bg-yellow-400 text-yellow-900">
                          Winning Catch
                        </Badge>
                      )}
                      {catchItem.share_to_community === false && (
                        <Badge className="absolute top-3 right-3 bg-gray-800 text-white">
                          <Lock className="w-3 h-3 mr-1" /> Private
                        </Badge>
                      )}
                    </div>
                  )}
                  {!catchItem.photo_url && catchItem.share_to_community === false && (
                    <div className="px-5 pt-4">
                      <Badge className="bg-gray-100 text-gray-600">
                        <Lock className="w-3 h-3 mr-1" /> Private
                      </Badge>
                    </div>
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-[var(--deep-ocean)]">{catchItem.species}</h3>
                        <Badge className={getFishingTypeBadgeClass(catchItem.fishing_type)}>
                          {catchItem.fishing_type}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(catchItem.id)}
                        data-testid={`delete-catch-${catchItem.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="space-y-2 text-sm text-[var(--river-stone)]">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{catchItem.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(catchItem.date_caught).toLocaleDateString()}</span>
                      </div>
                      {(catchItem.weight || catchItem.length) && (
                        <div className="flex items-center gap-4">
                          {catchItem.weight && (
                            <div className="flex items-center gap-1">
                              <Scale className="w-4 h-4" />
                              <span>{catchItem.weight} kg</span>
                            </div>
                          )}
                          {catchItem.length && (
                            <div className="flex items-center gap-1">
                              <Ruler className="w-4 h-4" />
                              <span>{catchItem.length} cm</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {catchItem.notes && (
                      <p className="mt-3 text-sm text-[var(--river-stone)] italic">
                        "{catchItem.notes}"
                      </p>
                    )}

                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                      <Heart className="w-4 h-4 text-[var(--river-stone)]" />
                      <span className="text-sm text-[var(--river-stone)]">{catchItem.likes} likes</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="bg-white card-shadow border-0">
            <CardContent className="p-12 text-center">
              <Fish className="w-16 h-16 text-[var(--river-stone)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[var(--deep-ocean)] mb-2">No catches yet</h3>
              <p className="text-[var(--river-stone)] mb-6">
                Start logging your catches to build your trophy room!
              </p>
              <Button
                onClick={() => setDialogOpen(true)}
                className="btn-primary"
                data-testid="log-first-catch-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Log Your First Catch
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default FishingLogPage;
